import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';
import TreatmentEditPopover from 'src/pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';

/**
 * ReasonForCallingLive - Component for capturing reason for call and treatment interest
 * - Reason for call: dropdown from practice choices "Practice Reasons For Calling"
 * - Treatment interest: uses TreatmentFlowComponent + TreatmentEditPopover for proper treatment selection
 */
const ReasonForCallingLive = ({
  formData,
  onChange,
  onTreatmentSelect,
  reasonOptions,
  disabled,
  required,
}) => {
  const theme = useTheme();
  const treatmentButtonRef = useRef(null);

  // Treatment popover state
  const [treatmentPopoverOpen, setTreatmentPopoverOpen] = useState(false);
  const [treatmentAnchorEl, setTreatmentAnchorEl] = useState(null);

  // Handle reason selection
  const handleReasonChange = useCallback((event) => {
    if (disabled) return;
    onChange('reasonForCall', event.target.value);
  }, [onChange, disabled]);

  // Handle treatment click to open popover
  const handleTreatmentClick = useCallback((event) => {
    if (disabled) return;
    setTreatmentAnchorEl(event?.currentTarget || treatmentButtonRef.current);
    setTreatmentPopoverOpen(true);
  }, [disabled]);

  // Handle treatment popover close
  const handleTreatmentClose = useCallback(() => {
    setTreatmentPopoverOpen(false);
    setTreatmentAnchorEl(null);
  }, []);

  // Handle treatment selection from popover
  const handleSaveTreatment = useCallback((treatmentInfo) => {
    if (disabled) return;

    // Update form data
    onChange('treatmentInterest', treatmentInfo.selectedTreatment || '');
    onChange('treatmentInterestValue', treatmentInfo.treatmentPrice || '');
    onChange('treatmentInterestTeeth', treatmentInfo.teethAmount || null);

    // Trigger treatment update callback if provided (to update lead)
    if (onTreatmentSelect) {
      onTreatmentSelect(treatmentInfo);
    }

    handleTreatmentClose();
  }, [onChange, onTreatmentSelect, disabled, handleTreatmentClose]);

  // Styles
  const fieldLabelSx = {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    mb: 0.75,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
      fontSize: '0.875rem',
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.light,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': {
      py: 1.25,
      px: 1.5,
    },
    '& .MuiSelect-select': {
      py: 1.25,
    },
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Row with Reason for call and Treatment interest */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {/* Reason for call */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Typography sx={fieldLabelSx}>
            <Iconify icon="solar:chat-round-call-bold" sx={{ width: 14, height: 14, color: '#7635DC' }} />
            Reason for call
            {required && <span style={{ color: '#FF5630', marginLeft: 2 }}>*</span>}
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={formData.reasonForCall || ''}
            onChange={handleReasonChange}
            disabled={disabled}
            sx={selectSx}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) => {
                if (!value) {
                  return (
                    <Typography sx={{ color: theme.palette.text.disabled, fontSize: '0.875rem' }}>
                      Select reason...
                    </Typography>
                  );
                }
                return value;
              },
            }}
          >
            {(reasonOptions || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.colour && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: option.colour,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography sx={{ fontSize: '0.875rem' }}>{option.label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Treatment interest - clickable to open treatment selector */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }} ref={treatmentButtonRef}>
          <Typography sx={fieldLabelSx}>
            <Iconify icon="mdi:tooth" sx={{ width: 14, height: 14, color: '#00A76F' }} />
            Treatment interest
          </Typography>

          {/* Treatment Popover */}
          {treatmentPopoverOpen && (
            <TreatmentEditPopover
              treatment={formData.treatmentInterest || ''}
              leadTreatmentSpecification={{ teeth_count: formData.treatmentInterestTeeth }}
              open={treatmentPopoverOpen}
              anchorEl={treatmentAnchorEl}
              handleClickAwayLister={handleTreatmentClose}
              treatmentPrice={formData.treatmentInterestValue}
              handleSaveTreatment={handleSaveTreatment}
              isFlow
              isScript
            />
          )}

          {/* Treatment Selector - Compact inline display */}
          <Box
            onClick={handleTreatmentClick}
            sx={{
              height: 42,
              borderRadius: '10px',
              border: `1px solid ${formData.treatmentInterest ? theme.palette.primary.main : theme.palette.divider}`,
              backgroundColor: formData.treatmentInterest ? `${theme.palette.primary.main}08` : theme.palette.background.paper,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              cursor: disabled ? 'default' : 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': disabled ? {} : {
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}08`,
              },
            }}
          >
            {!formData.treatmentInterest ? (
              <Typography sx={{ color: theme.palette.text.disabled, fontSize: '0.875rem' }}>
                Select treatment...
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#00A76F14',
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon="mdi:tooth" sx={{ width: 16, height: 16, color: '#00A76F' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      lineHeight: 1.2,
                    }}
                  >
                    {formData.treatmentInterest}
                  </Typography>
                  {formData.treatmentInterestValue && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: theme.palette.text.secondary,
                        lineHeight: 1.2,
                      }}
                    >
                      £ {formData.treatmentInterestValue}
                      {formData.treatmentInterestTeeth ? ` · ${formData.treatmentInterestTeeth} teeth` : ''}
                    </Typography>
                  )}
                </Box>
                <Iconify
                  icon="solar:pen-linear"
                  sx={{ width: 16, height: 16, color: theme.palette.primary.main, flexShrink: 0 }}
                />
              </Box>
            )}
            {!formData.treatmentInterest && (
              <Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20, color: theme.palette.text.disabled }} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ReasonForCallingLive.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  onTreatmentSelect: PropTypes.func,
  reasonOptions: PropTypes.array,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

ReasonForCallingLive.defaultProps = {
  formData: {},
  onChange: () => {},
  onTreatmentSelect: null,
  reasonOptions: [],
  disabled: false,
  required: false,
};

export default ReasonForCallingLive;
